import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Get the current user's access token for API authentication
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Make an authenticated API request
 */
async function apiRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers: headers as HeadersInit,
  });
}

/**
 * Get current credit balance for the authenticated user
 */
export async function getUserCredits(): Promise<{ credits: number; error?: string }> {
  try {
    const response = await apiRequest('/api/payment/user-credits', {
      method: 'GET',
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to get credits' }));
      return { credits: 0, error: errorData.error || 'Failed to get credits' };
    }
    
    const data = await response.json();
    return { credits: data.credits || 0 };
  } catch (error) {
    console.error('Error getting user credits:', error);
    return { credits: 0, error: 'Network error' };
  }
}

/**
 * Create a Stripe checkout session for credit purchase
 * @param amount Amount in EUR (1, 10, 25, or 50)
 */
export async function createCheckoutSession(amount: number): Promise<{ url?: string; sessionId?: string; error?: string }> {
  try {
    const response = await apiRequest('/api/payment/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to create checkout session' }));
      return { error: errorData.error || 'Failed to create checkout session' };
    }
    
    const data = await response.json();
    return { url: data.url, sessionId: data.session_id };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return { error: 'Network error' };
  }
}

/**
 * Redirect user to Stripe Checkout page
 */
export function redirectToCheckout(checkoutUrl: string): void {
  if (checkoutUrl) {
    window.location.href = checkoutUrl;
  }
}

